import {
  Button,
  Card,
  Form,
  Input,
  Typography,
  Layout,
  Progress,
} from "antd";
import { LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SET_PASSWORD } from "../../services/apiEndPoints";
import { toastSuccess, toastError, toastWarning } from "../../utils/toast";

const { Title, Text } = Typography;
const { Content } = Layout;

function getPasswordStrength(password: string) {
  let score = 0;

  if (!password) return { score: 0, label: "Weak", color: "#ff4d4f" };

  if (password.length >= 8) score += 25;
  if (/[A-Z]/.test(password)) score += 25;
  if (/[0-9]/.test(password)) score += 25;
  if (/[^A-Za-z0-9]/.test(password)) score += 25;

  if (score <= 25) return { score, label: "Weak", color: "#ff4d4f" };
  if (score <= 50) return { score, label: "Fair", color: "#faad14" };
  if (score <= 75) return { score, label: "Good", color: "#1890ff" };

  return { score, label: "Strong", color: "#52c41a" };
}

export default function SetPasswordPage() {
  const [password, setPassword] = useState("");
  const strength = getPasswordStrength(password);
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const memberId = searchParams.get("memberId");

  const handleSubmit = async () => {
    if (!token || !memberId || !password) return;

    if (strength.score < 50) {
      toastWarning("Please choose a stronger password");
      return;
    }

    try {
      const response = await fetch(SET_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberId, token, password }),
      });

      if (!response.ok) throw new Error("Failed to set password");

      toastSuccess("Password has been set successfully");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    } catch {
      toastError("Failed to set password. Please try again.");
    }
  };

  return (
    <Layout className="login-layout">
      {/* Decorative Side */}
      <div className="login-decor">
        <div className="decor-circle decor-1" />
        <div className="decor-circle decor-2" />
        <div className="decor-circle decor-3" />

        <div className="decor-content">
          <div className="decor-logo">
            <img
              src="/logo.png"
              alt="Logo"
              style={{ height: 48, width: 48, objectFit: "contain" }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <Title level={2} style={{ color: "#fff", marginBottom: 8 }}>
            Access Warehousing
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 15 }}>
            Create a strong password to secure your account
          </Text>
        </div>
      </div>

      {/* Set Password Form */}
      <Content className="login-content">
        <Card className="login-card" styles={{ body: { padding: "32px 28px" } }}>
          <div style={{ marginBottom: 24 }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              Set Password
            </Title>
            <Text type="secondary">
              Please enter and confirm your new password below.
            </Text>
          </div>

          <Form layout="vertical">
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: "Enter your new password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Enter new password"
                size="large"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            {password && (
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={strength.score}
                  showInfo={false}
                  strokeColor={strength.color}
                  size="small"
                />
                <Text style={{ color: strength.color, fontSize: 12 }}>
                  Strength: {strength.label}
                </Text>
              </div>
            )}

            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Confirm new password"
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                onClick={handleSubmit}
                style={{ height: 44, fontWeight: 600 }}
              >
                Set Password
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center" }}>
              <Link to="/login">
                <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }}>
                  Back to Sign In
                </Button>
              </Link>
            </div>

            <Text
              type="secondary"
              style={{
                display: "block",
                textAlign: "center",
                marginTop: 24,
                fontSize: 12,
              }}
            >
              &copy; {new Date().getFullYear()} Access Warehousing
            </Text>
          </Form>
        </Card>
      </Content>
    </Layout>
  );
}
